import { Link } from "react-router-dom";
import { ArrowRight, Baby, Sparkles, Stethoscope, HeartPulse, Flower2, HandHeart, ChevronRight } from "lucide-react";
import Placeholder from "@/components/Placeholder";
import { Reveal, Magnetic, StaggerGroup, StaggerItem, GlowCard, AnimatedLine, FloatingBlob } from "@/components/Motion";
import { services } from "@/lib/data";
import SEO from "@/components/SEO";

const iconMap = { Baby, Sparkles, Stethoscope, HeartPulse, Flower2, HandHeart };

export default function Services() {
  return (
    <div data-testid="services-page" className="relative overflow-hidden">
      <SEO title="Our Services" description="Comprehensive women's healthcare services from high-risk pregnancy to advanced laparoscopy." />
      <FloatingBlob color="aurora-purple" size={450} top="-5%" right="-5%" delay={0} />

      <section className="py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="overline mb-4">Services</div>
            <h1 className="font-heading text-5xl md:text-6xl text-[var(--color-text)] leading-[1.05] max-w-4xl">
              Specialized care for every <em className="text-[var(--color-coral)] not-italic">stage of life.</em>
            </h1>
            <p className="mt-6 text-lg text-[var(--color-text-muted)] max-w-2xl leading-relaxed">
              From the very first consultation to advanced surgical care — every offering is delivered with empathy, modern protocols, and the privacy you deserve.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 relative">
        <FloatingBlob color="aurora-coral" size={350} top="30%" left="-10%" delay={2} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-16">
          {services.map((s, i) => {
            const I = iconMap[s.icon] || HeartPulse;
            const reverse = i % 2 === 1;
            return (
              <Reveal key={s.slug} delay={i * 0.1}>
                <div
                  data-testid={`services-row-${s.slug}`}
                  className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
                >
                  <div className="lg:col-span-5">
                    <div className="img-hover-scale rounded-2xl border border-[var(--color-border-2)]">
                      <Placeholder variant="wide" label={s.title} src={s.image} />
                    </div>
                  </div>
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-10 h-10 rounded-xl bg-[var(--color-coral-light)] flex items-center justify-center border border-[var(--color-coral)]/20 shadow-[0_0_15px_var(--color-coral-glow)]">
                        <I className="w-5 h-5 text-[var(--color-coral)]" strokeWidth={1.6} />
                      </span>
                      <span className="text-xs tracking-[0.2em] uppercase text-[var(--color-coral)] font-semibold">0{i + 1} · Service</span>
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl text-[var(--color-text)] leading-tight">{s.title}</h2>
                    <p className="mt-4 text-base md:text-lg text-[var(--color-text-muted)] leading-relaxed">{s.short}</p>

                    <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                      {s.highlights.map((h) => (
                        <li key={h} className="text-sm text-[var(--color-text-muted)] flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-[var(--color-coral)] mt-0.5 shrink-0" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>

                    <Magnetic>
                      <Link
                        to={`/services/${s.slug}`}
                        data-testid={`services-detail-${s.slug}`}
                        className="mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[var(--color-coral)] hover:gap-3 transition-all"
                      >
                        Read full overview <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Magnetic>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="pb-24 relative">
        <FloatingBlob color="aurora-warm" size={400} bottom="10%" right="5%" delay={1} />
        <Reveal>
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="glass-card rounded-3xl p-10 lg:p-14 border-[var(--color-coral)]/30">
              <h2 className="font-heading text-3xl md:text-4xl text-white">Not sure which service fits?</h2>
              <p className="mt-3 text-white/80 max-w-xl">A short consultation will help map the right path. We&apos;ll listen first.</p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Magnetic><Link to="/appointment" data-testid="services-cta-book" className="btn-primary">Book Consultation</Link></Magnetic>
                <Magnetic><Link to="/contact" data-testid="services-cta-contact" className="btn-outline">Contact Us</Link></Magnetic>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
